import { computed } from "vue";

/**
 * Composable para persistência de filtros em localStorage
 * Reutilizável em qualquer componente com filtros (refs individuais ou objeto único)
 *
 * @param {string} storageKey - Chave única para localStorage (ex: 'compras-pedidos-filtros')
 * @param {object} filterRefs - Objeto com refs dos filtros (ex: { unidade, fornecedor, numero })
 *                              OU um ref único contendo um objeto de filtros
 * @param {object} initialValues - Valores iniciais dos filtros (estrutura deve bater com filterRefs)
 * @returns {object} { salvarFiltros, carregarFiltros, limparFiltros, filtrosAtivos }
 *
 * @example
 * // Opção A: Refs individuais
 * const { salvarFiltros, carregarFiltros, limparFiltros, filtrosAtivos } = useFilterPersistence(
 *   'compras-pedidos-filtros',
 *   { unidade: ref(null), fornecedor: ref(''), numero: ref('') },
 *   { unidade: null, fornecedor: '', numero: '' }
 * );
 *
 * // Opção B: Ref único com objeto de filtros
 * const filtros = ref({ unidade: null, fornecedor: '', numero: '' });
 * const { salvarFiltros, carregarFiltros, limparFiltros, filtrosAtivos } = useFilterPersistence(
 *   'compras-pedidos-filtros',
 *   filtros,
 *   { unidade: null, fornecedor: '', numero: '' }
 * );
 *
 * onMounted(() => carregarFiltros());
 * onActivated(() => carregarFiltros());
 * watch(() => filtros.value, () => { salvarFiltros(); refazerBusca(); }, { deep: true });
 */
export function useFilterPersistence(
  storageKey,
  filterRefs,
  initialValues = {}
) {
  // Detecta se é um ref único ou múltiplos refs
  const isSingleRef =
    filterRefs.value !== undefined &&
    typeof filterRefs.value === "object" &&
    !Array.isArray(filterRefs.value);

  // Salvar filtros em localStorage
  const salvarFiltros = () => {
    try {
      const filtrosAtuais = isSingleRef ? filterRefs.value : {};

      if (!isSingleRef) {
        Object.keys(filterRefs).forEach((key) => {
          filtrosAtuais[key] = filterRefs[key].value;
        });
      }

      localStorage.setItem(storageKey, JSON.stringify(filtrosAtuais));
    } catch (error) {
      console.error("Erro ao salvar filtros:", error);
    }
  };

  // Carregar filtros do localStorage
  const carregarFiltros = () => {
    try {
      const filtrosSalvos = localStorage.getItem(storageKey);
      if (!filtrosSalvos) return;

      const parsed = JSON.parse(filtrosSalvos);

      if (isSingleRef) {
        // Se é um ref único, mescla com valores iniciais
        filterRefs.value = { ...initialValues, ...parsed };
      } else {
        // Se são múltiplos refs, atualiza cada um
        Object.keys(filterRefs).forEach((key) => {
          if (key in parsed) {
            filterRefs[key].value =
              parsed[key] !== undefined ? parsed[key] : initialValues[key];
          }
        });
      }
    } catch (error) {
      console.error("Erro ao carregar filtros:", error);
      localStorage.removeItem(storageKey);
    }
  };

  // Limpar filtros
  const limparFiltros = () => {
    try {
      if (isSingleRef) {
        filterRefs.value = { ...initialValues };
      } else {
        Object.keys(filterRefs).forEach((key) => {
          filterRefs[key].value = initialValues[key];
        });
      }
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error("Erro ao limpar filtros:", error);
    }
  };

  // Computed que detecta se há filtros ativos (qualquer diferença dos valores iniciais)
  const filtrosAtivos = computed(() => {
    if (isSingleRef) {
      return Object.keys(initialValues).some((key) => {
        return (
          JSON.stringify(filterRefs.value[key]) !==
          JSON.stringify(initialValues[key])
        );
      });
    } else {
      return Object.keys(filterRefs).some((key) => {
        const atual = filterRefs[key].value;
        const inicial = initialValues[key];
        return JSON.stringify(atual) !== JSON.stringify(inicial);
      });
    }
  });

  return {
    salvarFiltros,
    carregarFiltros,
    limparFiltros,
    filtrosAtivos,
  };
}
